import type { RunProgress } from '../types/run.api.types';
import { isRunLive } from '../utils/run-status';

/**
 * catalog-enrichment polling policy — every interval a catalog-enrichment screen polls at lives here (see
 * docs/catalog-enrichment-polling.md). Rules shared by every poll:
 *  - polls never run in a hidden tab (`refetchIntervalInBackground: false`); a poll that fell due
 *    while hidden refetches once when the tab is shown again instead (`refetchOnShow`);
 *  - every interval is jittered ±15% per query (`jitteredPollMs`) so open tabs drift apart;
 *  - a terminal run / finished job stops polling; mutations invalidate their reads on settle.
 */

/** Run progress while the run moves on its own (PENDING / RUNNING / RESET). */
export const RUN_LIVE_POLL_MS = 4_000;

/**
 * Run progress while an open gate waits for a person (AWAITING_GATE). Only a decision moves it,
 * and decision mutations invalidate the run's reads on settle, so a slow poll only covers a
 * decision taken in another tab / by another reviewer.
 */
export const RUN_GATE_POLL_MS = 30_000;

/**
 * The summary funnel's cadence. While run progress polls, the summary is re-read at most this
 * often and only after progress MOVED (`progressSignature` changed) — never while it stands still
 * (an open gate). Once progress stops (terminal run, delivery still settling) the summary polls
 * itself at this cadence while its verdict is still active.
 */
export const FUNNEL_SUMMARY_POLL_MS = 5_000;

/**
 * Safety net while progress drives the summary: it also folds ledger gate outcomes and gate
 * rejections `progressSignature` may not see, so it is re-read at least this often (visible tab only).
 */
export const FUNNEL_SUMMARY_SAFETY_POLL_MS = 60_000;

/** Recovery failure points — same cadence as progress so the table and action cards move together. */
export const RECOVERY_LIVE_POLL_MS = RUN_LIVE_POLL_MS;

/** An experiment's embedded run progress — same cadence (and gate slow-down) as a run's progress. */
export const EXPERIMENT_LIVE_POLL_MS = RUN_LIVE_POLL_MS;

/** A running content stage's live detail, only while its panel is open. */
export const FUNNEL_CONTENT_POLL_MS = 5_000;

/** Performance-signal status while content-gen says the cards are recomputing. */
export const INSIGHTS_RECOMPUTING_POLL_MS = 10_000;

/** Runs list while a row on the page moves on its own — slower than one run's: a page is heavier. */
export const RUNS_LIST_MOVING_POLL_MS = 15_000;

/** Schedules dashboard — "running now" and next fires move on their own; visible tab only. */
export const SCHEDULES_OVERVIEW_POLL_MS = 30_000;

/** New-batch already-curated / scope check: first poll, growth factor per poll, and the cap. */
export const SCOPE_CHECK_POLL_INITIAL_MS = 1_500;
export const SCOPE_CHECK_POLL_FACTOR = 2;
export const SCOPE_CHECK_POLL_MAX_MS = 10_000;

/** True while the run is held at an open gate — nothing moves until a person decides. */
export function isWaitingOnPerson(runStatus: string | null | undefined): boolean {
  return runStatus === 'AWAITING_GATE';
}

/**
 * The poll interval for a run's live reads, before jitter: fast while it moves on its own, slow
 * while a gate waits on a person, `false` once terminal (or before the first answer).
 */
export function runPollBaseMs(runStatus: string | null | undefined): number | false {
  if (!isRunLive(runStatus)) return false;
  return isWaitingOnPerson(runStatus) ? RUN_GATE_POLL_MS : RUN_LIVE_POLL_MS;
}

/**
 * The scope-check backoff before jitter: 1.5 s, 3 s, 6 s, then 10 s for every later poll.
 * `answers` is how many answers the job has already given (TanStack's `dataUpdateCount`).
 */
export function scopeCheckPollBaseMs(answers: number): number {
  const step = Math.max(0, answers - 1);
  return Math.min(
    SCOPE_CHECK_POLL_MAX_MS,
    SCOPE_CHECK_POLL_INITIAL_MS * SCOPE_CHECK_POLL_FACTOR ** step,
  );
}

/**
 * `refetchOnWindowFocus` for a poll: when a hidden tab is shown again, re-read at once only if a
 * poll fell due while it was hidden; a query that is not polling keeps the app default (refetch
 * when stale).
 */
export function refetchOnShow(dataUpdatedAt: number, baseMs: number | false): boolean | 'always' {
  if (baseMs === false) return true;
  return Date.now() - dataUpdatedAt >= baseMs ? 'always' : false;
}

/** Clock-derived fields that change on every read without anything having moved. */
const VOLATILE_PROGRESS_KEYS = new Set(['elapsedMs']);

/**
 * What changed in a run's progress, minus the clock: two answers with the same signature mean the
 * run did not move, so the summary funnel built from the same snapshot need not be re-read.
 */
export function progressSignature(progress: RunProgress | undefined): string | null {
  if (!progress) return null;
  return JSON.stringify(progress, (key, value: unknown) =>
    VOLATILE_PROGRESS_KEYS.has(key) ? undefined : value,
  );
}
